import _ from 'lodash';
import type { Struct } from 'superstruct';
import { mask, string, trimmed, union } from 'superstruct';

import { isElementOf } from '@adflow/shared/util-js';
import {
	emptyStringIsNull,
	parsableArray,
	parsableInteger,
} from '@adflow/shared/util-superstruct';

import type { ParsedQ } from '../../nav-action';
import { firstValue } from '../../nav-action';

import { Lens } from './lens';

/**
 * Use this to specify the type that a lens is getting from and setting to.
 *
 * TypeScript cannot infer the type of some lenses because it does not know
 * what the lens will be used on. For example, `const myLens = atPath('foo')`
 * may cause a type error because TypeScript does not know what `myLens` is
 * meant to be used on. TypeScript has to guess and often guesses wrong.
 * Changing to `const myLens = lensFrom<MyObject>().then(atPath('foo'))` will
 * fix the issue. `lensFrom<MyObject>()` tells TypeScript that we are building
 * a lens to get from, and set to, values of type `MyObject`.
 */
export function lensFrom<S, T = S>() {
	return new Lens(
		(x: S) => x,
		(x: T) => x
	);
}

export function atPath<T extends object, K extends keyof T>(path: K) {
	return new Lens<T, T, T[K], T[K]>(
		(p) => _.get(p, path),
		(value, prev) => _.set(_.clone(prev), path, value)
	);
}

/**
 * Sort arrays and remove duplicate elements.
 *
 * This is a function instead of a const because functions can have generic types.
 */
export function sortUniqLens<S, T = S>() {
	return new Lens(
		(array: readonly S[]) => _.sortedUniq(array.toSorted()),
		(array: readonly T[]) => _.sortedUniq(array.toSorted())
	);
}

export function inArrayLens<S>(element: S) {
	return new Lens(
		(array: S[]) => array.includes(element),
		(isIn: boolean, array: S[]) =>
			isIn === array.includes(element)
				? array
				: isIn
					? [...array, element]
					: array.filter((e) => e !== element)
	);
}

export function enumLens<T>(elements: readonly T[], defaultElement: T) {
	return new Lens(
		(value: unknown) => (isElementOf(elements, value) ? value : defaultElement),
		(value: T) => value
	);
}

/**
 * Perform some additional modification to the source
 * when the lens is used to change its value.
 *
 * Note: A well behaved lens in functional programming would never have
 *       side effects like this. This function was used because it was
 *       convenient at the time. If lenses have gotten complicated try
 *       moving side effects out of the lenses.
 */
export function lensWithOnChange<S, T, A, B>(
	lens: Lens<S, T, A, B>,
	onChangeSideEffect: (s: S) => S,
	isEqual: (prev: A, next: B) => boolean = (a: unknown, b: unknown) => a === b
) {
	return new Lens<S, T, A, B>(lens.get, (b, s) => {
		const a = lens.get(s);

		if (!isEqual(a, b)) {
			s = onChangeSideEffect(s);
		}

		return lens.set(b, s);
	});
}

export function tryMask<T>(value: unknown, struct: Struct<T>): T | undefined {
	try {
		return mask(value, struct);
	} catch {
		return undefined;
	}
}

/**
 * Parse and stringify an array of integers.
 *
 * When parsing fails, this returns `[]`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseIntArrayLens() {
	return new Lens(
		(value: unknown) => tryMask(value, parsableArray(parsableInteger())) ?? [],
		(value: number[]) => (value.length > 0 ? value.join(',') : undefined)
	);
}

export function parseIntOrStrArrayLens() {
	return new Lens(
		(value: unknown) => {
			const items = Array.isArray(value)
				? value
				: typeof value === 'string'
					? value.split(',')
					: [];
			const parsedItems: (string | number)[] = [];

			for (const item of items) {
				if (typeof item === 'number' && Number.isInteger(item)) {
					parsedItems.push(item);
					continue;
				}

				const normalized = String(item).trim();

				if (!normalized) {
					continue;
				}

				parsedItems.push(
					/^-?\d+$/u.test(normalized)
						? Number.parseInt(normalized, 10)
						: normalized
				);
			}

			return parsedItems;
		},
		(value: (string | number)[]) =>
			value.length > 0 ? value.join(',') : undefined
	);
}

/**
 * Parse and stringify an integer.
 *
 * When parsing fails, this returns `undefined`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseIntLens() {
	return new Lens(
		(value: ParsedQ) => tryMask(firstValue(value), parsableInteger()),
		(value?: number) => (typeof value === 'number' ? '' + value : undefined)
	);
}

/**
 * Parse and stringify an integer or string.
 *
 * When parsing fails, this returns `undefined`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseIntOrStringLens() {
	return new Lens(
		(value: ParsedQ) =>
			tryMask(
				firstValue(value),
				union([parsableInteger(), trimmed(emptyStringIsNull(string()))])
			),
		(value?: string | number) =>
			typeof value === 'number'
				? '' + value
				: typeof value === 'string'
					? value
					: undefined
	);
}

/**
 * Parse and stringify an boolean.
 *
 * When parsing fails, this returns `undefined`.
 *
 * This was originally made to work with URL query parameters.
 * That is why it uses `string | undefined` instead of just `string`.
 */
export function parseBooleanLens() {
	return new Lens(
		(value: ParsedQ) => {
			if (value === 'false') return false;
			if (value === 'true') return true;
			return undefined;
		},
		(value?: boolean) => (typeof value === 'boolean' ? '' + value : undefined)
	);
}
